import Seo from "@/components/Seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, TrendingDown, FileText, Phone } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { Newsletter } from "@/components/Newsletter";
import { WEEKLY_DEALS, CONTACT } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function money(n) {
  return `$${Number(n).toFixed(Number.isInteger(n) ? 0 : 2)}`;
}

function savingsPct(our, comp) {
  return Math.round(((comp - our) / comp) * 100);
}

function DealCard({ deal, idx }) {
  const pct = savingsPct(deal.ourPrice, deal.compPrice);
  return (
    <div
      className="relative border border-white/10 bg-[#121212] p-7 hover:border-white/25 transition-colors duration-400 flex flex-col"
      data-testid="deal-card"
    >
      <div className="flex items-center justify-between gap-4 mb-6">
        <span className="text-xs uppercase tracking-[0.16em] text-white/40">{deal.category}</span>
        <span className="inline-flex items-center gap-1.5 bg-gold text-white text-xs font-semibold uppercase tracking-[0.1em] px-3 py-1.5">
          <TrendingDown className="w-3.5 h-3.5" /> Save {pct}%
        </span>
      </div>
      <h3 className="font-display uppercase tracking-tight leading-tight text-xl">
        {deal.name}
      </h3>
      <div className="mt-8 flex items-end gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.16em] text-gold/80 mb-1">Our price</p>
          <p className="font-display text-gold leading-none text-4xl">
            {money(deal.ourPrice)}
          </p>
        </div>
        <div className="pb-1">
          <p className="text-xs uppercase tracking-[0.16em] text-white/30 mb-1">{deal.competitor}</p>
          <p className="text-white/35 line-through leading-none text-2xl">
            {money(deal.compPrice)}
          </p>
        </div>
      </div>
      <div className="mt-auto pt-6 border-t border-white/10 flex flex-wrap items-center gap-3">
        <a
          href={CONTACT.phoneHref}
          data-testid={`deal-call-${idx}`}
          className="inline-flex items-center gap-2 bg-gold text-white text-xs uppercase tracking-[0.12em] font-semibold px-4 py-2.5 whitespace-nowrap hover:bg-[#b00000] transition-colors duration-300"
        >
          <Phone className="w-3.5 h-3.5" /> Call Now
        </a>
        <Link
          to="/contact"
          data-testid={`deal-quote-${idx}`}
          className="inline-flex items-center gap-2 border border-white/20 text-xs uppercase tracking-[0.12em] px-4 py-2.5 whitespace-nowrap hover:border-gold hover:text-gold transition-colors duration-300"
        >
          Get This Deal <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

export default function Deals() {
  const [flyer, setFlyer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/deals/flyer`)
      .then((res) => setFlyer(res.data.flyer))
      .catch(() => setFlyer(null))
      .finally(() => setLoading(false));
  }, []);

  const isPdf = flyer && flyer.content_type === "application/pdf";
  const flyerUrl = flyer ? `${process.env.REACT_APP_BACKEND_URL}${flyer.file_url}` : null;

  return (
    <div data-testid="deals-page">
      <Seo
        title="Weekly Lighting Deals in Toronto | Saver Plus Lighting Corp (SPLC)"
        description="See our weekly wholesale lighting deals and how our pricing compares to retail. No minimum order — the same price whether you need one item or one thousand. Toronto & the GTA."
        path="/deals"
      />
      <section className="pt-40 md:pt-56 pb-10 max-w-[1400px] mx-auto px-5 md:px-10">
        <p className="text-xs uppercase tracking-[0.28em] text-gold/90 font-semibold mb-6 flex items-center gap-3">
          <span className="inline-block w-8 h-px bg-gold" /> Weekly Deals
        </p>
        <h1 className="font-display uppercase font-medium text-[13vw] md:text-[8vw] leading-[0.85] tracking-tighter">
          <MaskedLines lines={["Wholesale", "prices that", "add up."]} once={false} />
        </h1>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.2}>
              <p className="text-lg text-white/70 leading-relaxed">
                See how our everyday wholesale pricing stacks up against retail. No minimum order — the same price
                whether you need one or one thousand.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee text="This Week's Deals" separator="·" />

      {/* Flyer */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24" data-testid="flyer-section">
        <div className="flex items-end justify-between gap-6 mb-10">
          <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-[0.95]">
            <MaskedLines lines={["This week's flyer"]} />
          </h2>
          <div className="hidden sm:flex flex-col items-end gap-2">
            <span className="text-xs uppercase tracking-[0.16em] text-white/40">{WEEKLY_DEALS.updated}</span>
            <a
              href={CONTACT.phoneHref}
              data-testid="flyer-call-cta"
              className="link-sweep inline-flex items-center gap-2 text-sm uppercase tracking-[0.14em] text-gold hover:text-white transition-colors duration-300"
            >
              <Phone className="w-4 h-4" /> Call {CONTACT.phone}
            </a>
          </div>
        </div>

        {loading ? (
          <div className="border border-white/10 bg-[#121212] aspect-[16/10] animate-pulse" />
        ) : flyer ? (
          <Reveal>
            {isPdf ? (
              <div className="border border-white/10 bg-[#121212] max-w-4xl mx-auto">
                <iframe title="Weekly deals flyer" src={flyerUrl} className="w-full h-[60vh] md:h-[80vh]" data-testid="flyer-pdf" />
                <a
                  href={flyerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-4 border-t border-white/10 text-sm uppercase tracking-[0.14em] text-gold hover:text-white transition-colors"
                  data-testid="flyer-view-full"
                >
                  <FileText className="w-4 h-4" /> Open full flyer (PDF)
                </a>
              </div>
            ) : (
              <div className="border border-white/10 bg-[#121212] max-w-3xl mx-auto">
                <a href={flyerUrl} target="_blank" rel="noopener noreferrer" className="block overflow-hidden group">
                  <img
                    src={flyerUrl}
                    alt="This week's deals flyer"
                    className="w-full h-auto group-hover:opacity-90 transition-opacity duration-300"
                    data-testid="flyer-image"
                  />
                </a>
                <a
                  href={flyerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-4 border-t border-white/10 text-sm uppercase tracking-[0.14em] text-gold hover:text-white transition-colors"
                  data-testid="flyer-view-full"
                >
                  <ArrowUpRight className="w-4 h-4" /> View full flyer
                </a>
              </div>
            )}
          </Reveal>
        ) : (
          <div className="border border-white/10 bg-[#121212] py-20 md:py-28 px-6 text-center" data-testid="flyer-empty">
            <p className="text-xs uppercase tracking-[0.28em] text-gold/90 font-semibold mb-6 flex items-center justify-center gap-3">
              <span className="inline-block w-8 h-px bg-gold" /> This week&rsquo;s flyer <span className="inline-block w-8 h-px bg-gold" />
            </p>
            <p className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-[0.95] text-white">
              New deals coming soon.
            </p>
            <p className="mt-5 text-white/50 max-w-md mx-auto leading-relaxed">
              This week&rsquo;s flyer is being prepared — check back shortly, or ask us for a price anytime.
            </p>
            <Link
              to="/contact"
              className="link-sweep inline-flex items-center gap-2 mt-7 text-sm uppercase tracking-[0.14em] text-gold hover:text-white transition-colors duration-300"
              data-testid="flyer-empty-quote-link"
            >
              Request a Quote <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </section>

      {/* Price comparison */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-10">
        <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-[0.95] mb-10">
          <MaskedLines lines={["Price check"]} />
        </h2>
        <Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {WEEKLY_DEALS.deals.map((d, i) => (
              <Reveal key={d.name} delay={i * 0.08}>
                <DealCard deal={d} idx={i} />
              </Reveal>
            ))}
          </div>
        </Reveal>
        <p className="mt-8 text-xs text-white/35 leading-relaxed max-w-2xl">{WEEKLY_DEALS.note}</p>
      </section>

      {/* Newsletter */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24" data-testid="deals-newsletter">
        <div className="border border-white/10 bg-[#121212] p-10 md:p-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.2em] text-gold/80 font-semibold mb-4">Never miss a deal</p>
            <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight leading-[0.95]">
              Get the weekly flyer by email
            </h2>
            <p className="mt-4 text-white/55 leading-relaxed">
              Subscribe and we'll send this week's flyer straight to your inbox — plus every new deal as it drops.
            </p>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-center">
            <Newsletter variant="block" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="border border-white/10 bg-[#121212] p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-[0.95] max-w-xl">
            Want pricing on something specific?
          </h2>
          <Link
            to="/contact"
            data-testid="deals-quote-cta"
            className="inline-flex items-center gap-2 bg-gold text-white text-sm uppercase tracking-[0.14em] font-semibold px-7 py-4 hover:bg-[#b00000] transition-colors duration-300 shrink-0"
          >
            Request a Quote <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
